import { FC } from "react";
import { Box, Tooltip, Typography } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { Link as LinkIcon, LinkOff as LinkOffIcon } from "@material-ui/icons";
import { find, some } from "lodash";
import moment from "moment";

import { CredentialSubmissionStatus } from "components";
import RoyaltyPayorImage from "components/RoyaltyPayorImage";
import { DASHED_DATE_FORMAT } from "constants/index";
import {
  AssetKYWCredentialSubmission,
  LastScraperTaskStatus,
} from "services/assets/types";
import { useRoyaltyPayors } from "services/royaltyPayor";
import theme from "theme";

const useStyles = makeStyles(() =>
  createStyles({
    accountInfoContainer: {
      display: "flex",
      marginBottom: theme.spacing(2),
      flexGrow: 1,
      textAlign: "left",
      width: "94%",
      paddingRight: theme.spacing(1),
    },
    accountInfos: {
      display: "flex",
      flexDirection: "column",
      maxWidth: "60%",
    },
    h6: { fontSize: "1.125rem", fontWeight: theme.typography.fontWeightMedium },
    h2: { fontSize: "2rem", fontWeight: theme.typography.fontWeightMedium },
  }),
);

interface AccountInfoProps {
  royaltyPayorDisplayName: string;
  username: string;
  subAccountName?: string;
  firstPaymentDate?: string;
  subtitle?: string;
  connectionStatus?: LastScraperTaskStatus;
  asset?: AssetKYWCredentialSubmission;
}

const AccountInfo: FC<AccountInfoProps> = ({
  royaltyPayorDisplayName,
  username,
  subAccountName,
  firstPaymentDate,
  subtitle,
  connectionStatus,
  asset,
}) => {
  const classes = useStyles();
  const { data: royaltyPayors } = useRoyaltyPayors();
  const isConnected = [
    "Royalty Information Up To Date",
    "Connection Lost - Temporary outage",
  ].includes(connectionStatus?.message);

  const royaltyPayor = find(royaltyPayors, {
    display_name: royaltyPayorDisplayName,
  });

  const hasGetCredentialsPermission = some(royaltyPayor?.permissions, {
    codename: "get_credentials",
  });

  const showConnectionStatus =
    royaltyPayor && connectionStatus && !hasGetCredentialsPermission;

  return (
    <Box className={classes.accountInfoContainer}>
      {royaltyPayorDisplayName && (
        <Box marginRight={`${theme.spacing(2)}px`} display="flex">
          <RoyaltyPayorImage
            containerPadding={false}
            height="67px"
            minWidth="100px"
            royaltyPayor={royaltyPayor}
            displayCanGetValuationIcon={false}
          />
        </Box>
      )}
      <Box className={classes.accountInfos}>
        <Typography variant="h6" className={classes.h6} color="primary" noWrap>
          {username}
        </Typography>
        {subAccountName && (
          <Tooltip title={subAccountName || ""}>
            <Typography
              color="primary"
              variant="h6"
              noWrap
              style={{
                fontSize: "0.8rem",
                textTransform: "uppercase",
                lineHeight: 1.4,
              }}
            >
              {subAccountName}
            </Typography>
          </Tooltip>
        )}
        {firstPaymentDate && (
          <Typography
            color="primary"
            variant="body1"
            style={{ fontSize: "0.9rem", lineHeight: 1.4 }}
          >
            Earning Since{" "}
            {moment
              .utc(firstPaymentDate, DASHED_DATE_FORMAT)
              .format("MMMM YYYY")}
          </Typography>
        )}
        {subtitle && (
          <Typography
            color="primary"
            variant="body1"
            style={{ fontSize: "0.9rem", lineHeight: 1.4 }}
          >
            {subtitle}
          </Typography>
        )}
        {showConnectionStatus && (
          <Box display="flex" alignItems="center">
            {isConnected ? (
              <LinkIcon style={{ color: theme.custom.palette.green.main }} />
            ) : (
              <LinkOffIcon style={{ color: theme.custom.palette.red.main }} />
            )}
            <Box ml={0.5}>
              <Typography
                color="primary"
                variant="body1"
                component="div"
                style={{ fontSize: "0.8rem", lineHeight: 1.4 }}
              >
                {isConnected ? "Connected" : "Connection Lost!"}
              </Typography>
            </Box>
          </Box>
        )}
        {!!asset && <CredentialSubmissionStatus assetState={asset.state} />}
      </Box>
    </Box>
  );
};

export default AccountInfo;
